import {
  HttpContext, HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';

/** Marks a request as needing the bearer token. Defaults to off — auth is opt-in. */
export const AUTHENTICATE = new HttpContextToken<boolean>(() => false);

/**
 * Shorthand for the common case: `this.http.get(url, { context: AUTHENTICATED })`.
 *
 * One shared instance, as `HttpContext` is mutable — never call `.set()` on it, that would
 * change the flag for every request using it. Build a fresh `new HttpContext()` and set
 * `AUTHENTICATE` yourself when a request needs to carry further context values too.
 */
export const AUTHENTICATED = new HttpContext().set(AUTHENTICATE, true);

/**
 * Waits for the startup/resume token check, appends the bearer token as an `Authorization`
 * header and, upon receiving a 401 response, attempts exactly ONE single-flight refresh +
 * retry. The retry is not protected against failure — a second 401 is propagated as an
 * error (intentional, to prevent an infinite loop).
 *
 * Requests without the `AUTHENTICATE` flag pass straight through and wait for nothing: a
 * public endpoint has no use for the token check, and a 401 from it triggers no refresh —
 * an unauthenticated endpoint rejecting a call says nothing about our tokens.
 *
 * The wait can live here because `AuthService` speaks to the IdP via `fetch`. Were it to
 * use `HttpClient`, its own refresh request would land in this interceptor and deadlock
 * waiting for the refresh it is supposed to perform.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.context.get(AUTHENTICATE)) return next(req);

  const authService = inject(AuthService);

  const withToken = (httpRequest: HttpRequest<unknown>, token: string | null) =>
    token ? httpRequest.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : httpRequest;

  return authService.waitUntilAuthSettled().pipe(
    switchMap(() => from(authService.getValidAccessToken())),
    switchMap(token => next(withToken(req, token))),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return from(authService.refresh()).pipe(
          switchMap(tokens => next(withToken(req, tokens.accessToken))),
        );
      }
      return throwError(() => err);
    }),
  );
};
